import { useEffect, useRef, useState } from 'react'
import { KLASSRUM_DELNINGSLANK, TEAMS_DELNINGSLANK, laddaSkript, vantaPa } from '../lib/dela'

/** Delningsdialog för ett kapitel: kopiera direktlänk, QR-kod samt de
 *  officiella delningsknapparna för Google Classroom och Microsoft Teams.
 *  Om tredjepartsskripten inte går att ladda visas reservlänkar i stället. */
export default function DelaKapitel({ titel, onStang }: { titel: string; onStang: () => void }) {
  const url = window.location.href
  const [kopierad, setKopierad] = useState(false)
  const [classroomFallback, setClassroomFallback] = useState(false)
  const [teamsFallback, setTeamsFallback] = useState(false)
  const qrRef = useRef<HTMLCanvasElement>(null)
  const classroomRef = useRef<HTMLDivElement>(null)
  const teamsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const vidTangent = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onStang()
    }
    document.addEventListener('keydown', vidTangent)
    return () => document.removeEventListener('keydown', vidTangent)
  }, [onStang])

  // QR-koden genereras lokalt i webbläsaren — ingen extern tjänst.
  // Biblioteket laddas först när dialogen öppnas (utanför huvudbundeln).
  useEffect(() => {
    let aktiv = true
    import('qrcode').then(({ default: QRCode }) => {
      if (aktiv && qrRef.current) {
        QRCode.toCanvas(qrRef.current, url, { width: 176, margin: 1 }).catch(() => {})
      }
    })
    return () => {
      aktiv = false
    }
  }, [url])

  // Google Classroom: platform.js + explicit rendering i vår container.
  useEffect(() => {
    let aktiv = true
    laddaSkript('https://apis.google.com/js/platform.js')
      .then(() => vantaPa(() => window.gapi?.sharetoclassroom))
      .then((sharetoclassroom) => {
        if (aktiv && classroomRef.current) sharetoclassroom.render(classroomRef.current, { url, size: 48 })
      })
      .catch(() => {
        if (aktiv) setClassroomFallback(true)
      })
    return () => {
      aktiv = false
    }
  }, [url])

  // Microsoft Teams: launcher.js hittar .teams-share-button via renderButtons.
  useEffect(() => {
    let aktiv = true
    laddaSkript('https://teams.microsoft.com/share/launcher.js')
      .then(() => vantaPa(() => window.shareToMicrosoftTeams))
      .then((teams) => {
        if (aktiv && teamsRef.current) teams.renderButtons({ elements: [teamsRef.current] })
      })
      .catch(() => {
        if (aktiv) setTeamsFallback(true)
      })
    return () => {
      aktiv = false
    }
  }, [url])

  const kopiera = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setKopierad(true)
      setTimeout(() => setKopierad(false), 2500)
    } catch {
      // Om Clipboard-API:t inte är tillgängligt får användaren markera texten själv.
    }
  }

  return (
    <div className="dela-overlagg" role="presentation" onClick={(e) => e.target === e.currentTarget && onStang()}>
      <div className="dela-dialog" role="dialog" aria-modal="true" aria-label={`Dela kapitlet ${titel}`}>
        <div className="dela-huvud">
          <h2>Dela kapitlet</h2>
          <button type="button" className="rund-knapp" onClick={onStang} aria-label="Stäng">
            ✕
          </button>
        </div>
        <p className="dela-titel">{titel}</p>

        <div className="dela-lank">
          <input type="text" readOnly value={url} aria-label="Direktlänk till kapitlet" onFocus={(e) => e.target.select()} />
          <button type="button" className="knapp knapp-primar knapp-liten" onClick={kopiera}>
            {kopierad ? '✓ Kopierad' : 'Kopiera länk'}
          </button>
        </div>

        <div className="dela-rad">
          <div className="dela-qr">
            <canvas ref={qrRef} aria-label={`QR-kod som leder till kapitlet ${titel}`} />
            <p>Låt eleverna skanna med mobilkameran</p>
          </div>

          <div className="dela-tjanster">
            <h3>Dela i</h3>
            <div className="dela-tjanst">
              {classroomFallback ? (
                <a className="knapp knapp-sekundar knapp-liten" href={KLASSRUM_DELNINGSLANK(url)} target="_blank" rel="noreferrer">
                  Google Classroom ↗
                </a>
              ) : (
                <>
                  <div ref={classroomRef} className="dela-knappyta" aria-label="Dela i Google Classroom" />
                  <span>Google Classroom</span>
                </>
              )}
            </div>
            <div className="dela-tjanst">
              {teamsFallback ? (
                <a
                  className="knapp knapp-sekundar knapp-liten"
                  href={TEAMS_DELNINGSLANK(url, titel)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Microsoft Teams ↗
                </a>
              ) : (
                <>
                  <div
                    ref={teamsRef}
                    className="teams-share-button dela-knappyta"
                    data-href={url}
                    data-msg-text={titel}
                    data-icon-px-size="48"
                    aria-label="Dela i Microsoft Teams"
                  />
                  <span>Microsoft Teams</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
